import Vue from "vue";
import { boot } from "quasar/wrappers";
import axios from "axios";
import { i18n } from "boot/i18n";

const api = axios.create({
  baseURL: process.env.VUE_APP_MAIN_URL
});

// One id per browser tab, used purely to correlate BE log lines into a single
// user flow (login -> GETs -> a failed save -> retry) without FE analytics
// tooling. Re-read (not cached) on every request so a cleared sessionStorage
// (e.g. logout) naturally starts a new one on the next call instead of
// silently merging a new login into the previous session's id.
const SESSION_ID_KEY = "amt-viot-session-id";
function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
}
Vue.prototype.$axios = axios;
// ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
//       so you won't necessarily have to import axios in each vue file
Vue.prototype.$api = api;

// ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
//       so you can easily perform requests against your app's API

export default ({ app, store, router }) => {
  const performLogout = reason => {
    console.warn(reason);
    store.dispatch("userCenter/logout");
  };

  // The BE also answers 401 for permission/business denials, so a 401 alone
  // doesn't prove the JWT is dead. Ask /users/me (bypassing the interceptors)
  // and only treat a 401 there as an expired session. Concurrent 401s share
  // one check.
  let tokenCheck = null;
  const isTokenRejected = token => {
    if (!tokenCheck) {
      tokenCheck = axios
        .get(`${api.defaults.baseURL}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(() => false)
        .catch(err => !!err.response && err.response.status === 401)
        .finally(() => {
          tokenCheck = null;
        });
    }
    return tokenCheck;
  };

  // for use inside Vue files (Options API) through this.$axios and this.$api
  api.interceptors.request.use(config => {
    var token =
      !!store.state.userCenter.user && store.state.userCenter.user.jwt;
    if (token) {
      // Checking if the url is not login because we should have Auth header there. Login witll not work
      if (
        config.url !== "/api/auth/local" &&
        config.url !== "/api/maintenance-mode"
      ) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        config.headers.Authorization = null;
      }
    }
    config.headers["Accept-Language"] = i18n.locale === "en-us" ? "en" : "de";
    const sessionId = getSessionId();
    if (sessionId) {
      config.headers["X-Session-Id"] = sessionId;
    }
    return config;
  });

  // Response interceptor to handle authentication errors
  api.interceptors.response.use(
    response => {
      // Return successful responses as-is
      return response;
    },
    async error => {
      const token = store.state.userCenter.user && store.state.userCenter.user.jwt;
      const isLogin = error.config && error.config.url === "/api/auth/local";

      if (error.response && error.response.status === 401 && token && !isLogin) {
        if (await isTokenRejected(token)) {
          performLogout("Token expired or invalid, logging out user");
        }
      }

      return Promise.reject(error);
    }
  );
};
export { api, axios };
